import slugify from 'react-slugify'

import { F } from '../context'
import {
  collectionPrefix,
  generateRefreshKey,
  getParams,
  GetVariable,
  GetVariableValue,
  setInputParameter,
  Update,
} from '../hooks'
import { checkIdfaAndCookieTrackingEnabled, enableIdfaAndCookieTracking } from '../services/permissions'
import {
  Action,
  ActionType,
  findComponentByFieldName,
  getInputParameters,
  getSearch,
  InputParameter,
  Screen,
  ScreenParameter,
  showAlert,
  showError,
  startLoader,
  stopLoader,
  ValueType,
  VariableSourceType,
} from './index'

export const runActions = async (
  f: F,
  id: string,
  actions: Action[],
  navigate: (pathname: string) => void,
  navigateBack: () => void,
  openDialog: (page: string, beforeClose?: () => Promise<void>) => void,
  closeDialog: () => Promise<void>,
  closeAllDialogs: () => void,
  screens: Screen[],
  screenConfig: Screen,
  getVariableValue: GetVariableValue,
  getVariable: GetVariable,
  update: Update,
  indexInList?: number
) => {
  const {
    auth,
    signInWithGoogle,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendPasswordResetEmail,
    sendSignInLinkToEmail,
    reSendSignInLinkToEmail,
    checkNotificationsEnabled,
    enableNotifications,
    logout,
    runFlow,
    oauth,
    checkoutSelected,
    restorePurchases,
  } = f
  const checkIfScreenInputParameters = (variableName: string, value: any) => {
    const {
      location: { pathname, search },
      history,
    } = window
    const { screenName, inputParameters } = screenConfig
    const { inputParameter, searchInputParameters } = getInputParameters(inputParameters)
    const screenInputParameter = inputParameter?.parameter === variableName ? inputParameter : undefined
    const screenSearchInputParameter = searchInputParameters?.find(el => el.parameter === variableName)
    const modifyValue = (value: any, type: ValueType) =>
      type === ValueType.record ? `${collectionPrefix}${value.collectionId}/${value.records[0]?.id}` : String(value)
    if (screenInputParameter && value) {
      const { type } = screenInputParameter
      const pages = pathname.slice(1).split('/-/')
      pages[pages.length - 1] = `${slugify(screenName)}/${encodeURIComponent(modifyValue(value, type))}`
      history.replaceState(null, '', `/${pages.join('/-/')}` + search)
    }
    if (screenSearchInputParameter) {
      const { type, parameter } = screenSearchInputParameter
      const searchParams = new URLSearchParams(search)
      if (value !== '' && value !== null && value !== undefined) {
        searchParams.set(parameter, modifyValue(value, type))
      } else {
        searchParams.delete(parameter)
      }
      history.replaceState(null, '', pathname + searchParams.toString())
    }
  }
  const setDialogParameters = async (dialog: string, screenParameters: InputParameter[]) => {
    const screenConfig = screens.find(el => el.screenName === dialog)
    if (screenConfig) {
      const { id, inputParameters } = screenConfig
      const { inputParameter, searchInputParameters, inoutInputParameters } = getInputParameters(inputParameters)
      if (inputParameter) {
        const parameter = screenParameters.find(el => inputParameter.parameter === el.name)
        if (parameter) {
          const value = await getVariableValue(parameter.value)
          await setInputParameter(id, getVariable(screenConfig), inputParameter, value)
        }
      }
      if (searchInputParameters) {
        const searchParameters = screenParameters.filter(
          el => !!searchInputParameters.find(searchInputParameter => el.name === searchInputParameter.parameter)
        )
        for (const searchParameter of searchParameters) {
          const searchInputParameter = searchInputParameters.find(
            el => el.parameter === searchParameter.name
          ) as ScreenParameter
          const value = await getVariableValue(searchParameter.value)
          await setInputParameter(id, getVariable(screenConfig), searchInputParameter, value)
        }
      }
      if (inoutInputParameters) {
        const inoutParameters = screenParameters.filter(
          el => !!inoutInputParameters.find(inoutParameter => el.name === inoutParameter.parameter)
        )
        for (const el of inoutParameters) {
          const variable = { source: { type: VariableSourceType.localVariable, variableName: el.name } }
          const value = await getVariableValue(el.value, undefined, undefined, true)
          await getVariable(screenConfig)(variable, undefined, { value }, true)
          const refresh = async () => {
            const value = await getVariable(screenConfig)(
              variable,
              { [generateRefreshKey(id, `${el.name}/variable`, indexInList)]: refresh },
              undefined,
              true
            )
            const variableName = el.value?.source?.variableName
            if (variableName) {
              checkIfScreenInputParameters(variableName, value)
            }
            await getVariableValue(el.value, undefined, { value })
          }
          await refresh()
        }
      }
    }
  }
  try {
    for (let i = 0; i < actions.length; i++) {
      const action = actions[i]
      const {
        actionType,
        options,
        componentName,
        parameters,
        screenParameters,
        variable,
        variableContextKey,
        valueTarget,
        subActions,
        fallbackActions,
        asynchronous,
      } = action
      const email = options?.email
      const screen = options?.screen
      const dialog = options?.dialog
      const name = options?.name
      const link = options?.link
      if (
        (actionType === ActionType.check && (await getVariableValue(variable)) === false) ||
        (actionType === ActionType.checkNotificationsEnabled && (await checkNotificationsEnabled()) === false) ||
        (actionType === ActionType.checkIdfaAndCookieTrackingEnabled && checkIdfaAndCookieTrackingEnabled() === false)
      ) {
        if (fallbackActions) {
          const cancelled = await runActions(
            f,
            id,
            fallbackActions,
            navigate,
            navigateBack,
            openDialog,
            closeDialog,
            closeAllDialogs,
            screens,
            screenConfig,
            getVariableValue,
            getVariable,
            update,
            indexInList
          )
          if (cancelled) {
            return true
          }
        }
        break
      } else if (actionType === ActionType.handleDeeplink) {
        const { pathname, search } = window.location
        const { screenName } = screenConfig
        if (pathname !== '/' && pathname !== `/${slugify(screenName)}`) {
          navigate(pathname + search)
          return true
        } else {
          if (fallbackActions) {
            const cancelled = await runActions(
              f,
              id,
              fallbackActions,
              navigate,
              navigateBack,
              openDialog,
              closeDialog,
              closeAllDialogs,
              screens,
              screenConfig,
              getVariableValue,
              getVariable,
              update,
              indexInList
            )
            if (cancelled) {
              return true
            }
          }
          break
        }
      } else if (actionType === ActionType.subscribe && variable && variableContextKey) {
        const refresh = async () => {
          try {
            const value = await getVariableValue(
              variable,
              { [generateRefreshKey(id, `${variableContextKey}/variable`, indexInList)]: refresh },
              undefined,
              true
            )
            await getVariableValue(
              { source: { type: VariableSourceType.localVariable, variableName: variableContextKey } },
              undefined,
              { value },
              true
            )
            if (subActions) {
              const cancelled = await runActions(
                f,
                id,
                subActions,
                navigate,
                navigateBack,
                openDialog,
                closeDialog,
                closeAllDialogs,
                screens,
                screenConfig,
                getVariableValue,
                getVariable,
                update,
                indexInList
              )
              if (cancelled) {
                return true
              }
            }
          } catch (err: any) {
            showError(err)
          }
        }
        const cancelled = await refresh()
        if (cancelled) {
          return true
        }
      } else if (actionType === ActionType.checkAccessLevels && dialog && parameters && fallbackActions) {
        const checkIfNoAccessLevels = async () => {
          const { levels, requiredLevels } = await getParams(getVariableValue, parameters)
          return (
            Array.isArray(requiredLevels) &&
            !requiredLevels.every((el: string) => Array.isArray(levels) && levels.includes(el))
          )
        }
        const cancelled = await new Promise(async resolve => {
          if (await checkIfNoAccessLevels()) {
            if (screenParameters) {
              await setDialogParameters(dialog, screenParameters)
            }
            openDialog(slugify(dialog), async () => {
              if (await checkIfNoAccessLevels()) {
                const cancelled = await runActions(
                  f,
                  id,
                  fallbackActions,
                  navigate,
                  navigateBack,
                  openDialog,
                  closeDialog,
                  closeAllDialogs,
                  screens,
                  screenConfig,
                  getVariableValue,
                  getVariable,
                  update,
                  indexInList
                )
                if (cancelled) {
                  resolve(true)
                } else {
                  resolve(null)
                }
              } else {
                resolve(null)
              }
            })
          } else {
            resolve(null)
          }
        })
        if (cancelled) {
          return true
        }
      } else if (actionType === ActionType.show && screen) {
        navigate(`/${slugify(screen)}`)
        return true
      } else if (actionType === ActionType.navigate && screen) {
        let to = `-/${slugify(screen)}`
        if (screenParameters) {
          const screenConfig = screens.find(el => el.screenName === screen)
          if (screenConfig) {
            const { inputParameters } = screenConfig
            const { inputParameter, searchInputParameters } = getInputParameters(inputParameters)
            if (inputParameter) {
              const parameter = screenParameters.find(el => inputParameter.parameter === el.name)
              if (parameter) {
                const value = await getVariableValue(parameter.value)
                to += `/${encodeURIComponent(value)}`
              }
            }
            if (searchInputParameters) {
              const searchParameters = screenParameters.filter(
                el => !!searchInputParameters.find(searchInputParameter => el.name === searchInputParameter.parameter)
              )
              const params = await getParams(getVariableValue, searchParameters)
              to += getSearch(params)
            }
          }
        }
        navigate(to)
        return true
      } else if (actionType === ActionType.navigateBack) {
        navigateBack()
        return true
      } else if (actionType === ActionType.openDialog && dialog) {
        if (screenParameters) {
          await setDialogParameters(dialog, screenParameters)
        }
        openDialog(slugify(dialog))
      } else if (actionType === ActionType.closeDialog) {
        await closeDialog()
      } else if (actionType === ActionType.closeAllDialogs) {
        closeAllDialogs()
      } else if (actionType === ActionType.setValue && valueTarget && variable) {
        const value = await getVariableValue(variable, undefined, undefined, true)
        const variableName = valueTarget.variableName
        if (variableName) {
          checkIfScreenInputParameters(variableName, value)
        }
        await getVariableValue({ source: valueTarget }, undefined, { value })
      } else if (actionType === ActionType.clearValue && valueTarget) {
        const value = null
        const variableName = valueTarget.variableName
        if (variableName) {
          checkIfScreenInputParameters(variableName, value)
        }
        await getVariableValue({ source: valueTarget }, undefined, { value })
      } else if (
        actionType === ActionType.getImage ||
        actionType === ActionType.getVideo ||
        actionType === ActionType.getAudio ||
        actionType === ActionType.getFile ||
        actionType === ActionType.createRecord ||
        actionType === ActionType.updateRecord ||
        actionType === ActionType.deleteRecord
      ) {
        await update(getVariableValue, action)
      } else if (actionType === ActionType.logout) {
        await logout()
      } else if (actionType === ActionType.anonymousAuthentication) {
        await auth.signInAnonymously()
      } else if (actionType === ActionType.googleAuthentication) {
        await signInWithGoogle()
      } else if (actionType === ActionType.createUserWithEmailAndPassword && parameters) {
        await createUserWithEmailAndPassword(getVariableValue, parameters)
      } else if (actionType === ActionType.signInWithEmailAndPassword && parameters) {
        await signInWithEmailAndPassword(getVariableValue, parameters)
      } else if (actionType === ActionType.sendPasswordResetEmail && variable) {
        await sendPasswordResetEmail(getVariableValue, variable)
      } else if (actionType === ActionType.sendSignInLinkToEmail && variable) {
        await sendSignInLinkToEmail(getVariableValue, variable)
      } else if (actionType === ActionType.reSendSignInLinkToEmail && variable) {
        await reSendSignInLinkToEmail(getVariableValue, variable)
      } else if (actionType === ActionType.runFlow && name) {
        const data = parameters ? await getParams(getVariableValue, parameters) : undefined
        if (asynchronous) {
          runFlow(`${name}?async=true`, data).catch(err => showError(err))
        } else {
          await runFlow(name, data)
        }
      } else if (actionType === ActionType.oauth && parameters && valueTarget) {
        await oauth(getVariableValue, parameters, valueTarget)
      } else if (actionType === ActionType.alert && parameters) {
        const { title, primaryButton, message, secondaryButton } = await getParams(getVariableValue, parameters)
        showAlert(title, primaryButton, message, secondaryButton).then(async res => {
          if (res) {
            if (subActions) {
              startLoader()
              await runActions(
                f,
                id,
                subActions,
                navigate,
                navigateBack,
                openDialog,
                closeDialog,
                closeAllDialogs,
                screens,
                screenConfig,
                getVariableValue,
                getVariable,
                update,
                indexInList
              )
              stopLoader()
            }
          } else {
            if (fallbackActions) {
              startLoader()
              await runActions(
                f,
                id,
                fallbackActions,
                navigate,
                navigateBack,
                openDialog,
                closeDialog,
                closeAllDialogs,
                screens,
                screenConfig,
                getVariableValue,
                getVariable,
                update,
                indexInList
              )
              stopLoader()
            }
          }
        })
      } else if (actionType === ActionType.enableNotifications) {
        await enableNotifications()
      } else if (actionType === ActionType.enableIdfaAndCookieTracking) {
        enableIdfaAndCookieTracking()
      } else if ((actionType === ActionType.openLink || actionType === ActionType.openWeb) && link) {
        window.open(await getVariableValue({ ...link, urlConstant: link.constant }))
      } else if (actionType === ActionType.openDocument && variable) {
        const variableValue = await getVariableValue(variable)
        if (variableValue) {
          window.open(variableValue.url)
        }
      } else if (actionType === ActionType.openEmail && email) {
        showAlert(
          'Action Required',
          'Open email app',
          `We can't open your email provider automatically in this browser. You can try opening an external email app using the button below, or manually compose an email to ${email}`,
          'Cancel'
        ).then(res => {
          if (res) {
            window.location.href = `mailto:${options.email}`
          }
        })
      } else if (actionType === ActionType.openEmailApp) {
        showAlert(
          'Action Required',
          'Got it',
          `We can't open your email provider automatically in this browser. Please open your email app manually.`
        )
      } else if (actionType === ActionType.checkoutSelected && variable) {
        const variableValue = await getVariableValue(variable)
        if (variableValue) {
          await checkoutSelected(getVariable, variableValue)
        }
      } else if (actionType === ActionType.restorePurchases) {
        await restorePurchases()
      } else if ((actionType === ActionType.scrollToStart || actionType === ActionType.scrollToEnd) && componentName) {
        const component = findComponentByFieldName(screenConfig, 'name', componentName)
        if (component) {
          const { reversed } = component
          const reversedValue = await getVariableValue({ ...reversed, booleanConstant: reversed?.constant })
          const elementToScroll = document.querySelector(`[id*=".${componentName}."]`)
          if (elementToScroll) {
            elementToScroll.scrollLeft =
              actionType === ActionType.scrollToStart
                ? !reversedValue
                  ? 0
                  : elementToScroll.scrollWidth
                : reversedValue
                  ? 0
                  : elementToScroll.scrollWidth
            elementToScroll.scrollTop =
              actionType === ActionType.scrollToStart
                ? !reversedValue
                  ? 0
                  : elementToScroll.scrollHeight
                : reversedValue
                  ? 0
                  : elementToScroll.scrollHeight
          }
        }
      }
    }
  } catch (err: any) {
    showError(err)
  }
}
